import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OneWayInOpportunityStatusCreateDto, OneWayInService } from 'src/api/v2';
import {
  ONE_WAY_IN_ALL_SAVED_FORMS_KEY,
  ONE_WAY_IN_FEEDBACK,
  ONE_WAY_IN_FORM_FULL_HISTORY,
  ONE_WAY_IN_OPPORTUNITY_BY_ID,
} from 'src/constants/hook-keys';

import { useAuth } from 'src/features/authorization';
import { HOTJAR_DECLARATIONS } from 'src/features/tracking/constants';
import useHotjar from 'src/hooks/use-hotjar';

const useOneWayInMutations = () => {
  const [oneWayInService] = useState(new OneWayInService());
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const { checkAccess } = useAuth();
  const hotjar = useHotjar();
  const { write } = checkAccess('OneWayIn');

  const updateOneWayInOpportunityStatus = useMutation({
    mutationFn: ({ updateDto }: { updateDto: OneWayInOpportunityStatusCreateDto }) => {
      if (!write) {
        throw new Error(tCommon('cannotWrite'));
      }
      if (updateDto.description !== undefined) {
        hotjar.log(`${HOTJAR_DECLARATIONS.UMUX.id} ${HOTJAR_DECLARATIONS.UMUX.actions.NOTE_ADDED}`);
      } else if (updateDto.registeredServices.length > 0) {
        hotjar.log(`${HOTJAR_DECLARATIONS.UMUX.id} ${HOTJAR_DECLARATIONS.UMUX.actions.SERVICE_DELIVERED}`);
      } else if (updateDto.recommendedServices.length > 0) {
        hotjar.log(`${HOTJAR_DECLARATIONS.UMUX.id} ${HOTJAR_DECLARATIONS.UMUX.actions.SERVICE_RECOMMENDED}`);
      }
      return oneWayInService.putWayInOpportunityStatus(updateDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);
      queryClient.invalidateQueries([ONE_WAY_IN_FORM_FULL_HISTORY]);
      queryClient.refetchQueries([ONE_WAY_IN_FEEDBACK]);
      return queryClient.invalidateQueries([ONE_WAY_IN_OPPORTUNITY_BY_ID]);
    },
  });

  return {
    updateOneWayInOpportunityStatus,
  };
};

export default useOneWayInMutations;
